/**
 * OperatorWorkTerminal - Touch-friendly job management terminal
 * Used by shop floor operators to log time on jobs
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Play, Pause, CheckCircle, LogOut, User, Factory, 
  Package, RefreshCw, ChevronLeft, Square, ArrowRightCircle 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OperatorWorkTerminal = () => {
  const [step, setStep] = useState(1); // 1=department, 2=select operator, 3=job list
  const [department, setDepartment] = useState('');
  const [password, setPassword] = useState('');
  const [departmentToken, setDepartmentToken] = useState('');
  const [departmentInfo, setDepartmentInfo] = useState(null);
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [token, setToken] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Support multiple active jobs - map of job_id -> { job, timer, isPaused, startedAt }
  const [activeJobs, setActiveJobs] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoRevertCountdown, setAutoRevertCountdown] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [jobToMove, setJobToMove] = useState(null);

  // Department to stage mapping
  const departmentStageMap = {
    'Litho': 'litho-print',
    'Digital': 'digital-print',
    'Wide Format': 'wide-format',
    'Finishing': 'finishing',
    'Prepress': 'prepress',
    'Admin': null,
  };
  
  // All available departments for "Move to Department"
  const allDepartments = [
    { name: 'Artwork', stage: 'artwork' },
    { name: 'Prepress', stage: 'prepress' },
    { name: 'Digital Print', stage: 'digital-print' },
    { name: 'Litho Print', stage: 'litho-print' },
    { name: 'Wide Format', stage: 'wide-format' },
    { name: 'Finishing', stage: 'finishing' },
    { name: 'Packaging', stage: 'packaging' },
    { name: 'Dispatch', stage: 'dispatch' },
  ];

  // Check for existing department session on mount (10-hour persistence)
  useEffect(() => {
    const savedSession = localStorage.getItem('workTerminalSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        const expiry = new Date(session.expiry);
        if (expiry > new Date()) {
          setDepartmentToken(session.departmentToken);
          setDepartmentInfo(session.departmentInfo);
          setOperators(session.operators);
          setStep(2);
        } else {
          localStorage.removeItem('workTerminalSession');
        }
      } catch (e) {
        localStorage.removeItem('workTerminalSession');
      }
    }
  }, []);

  // Update current time
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Timer effect - update all active job timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveJobs(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        Object.keys(updated).forEach(jobId => {
          if (!updated[jobId].isPaused) {
            updated[jobId] = { ...updated[jobId], timer: updated[jobId].timer + 1 };
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Heartbeat effect - keep backend tracking alive for all active jobs
  useEffect(() => {
    const activeJobIds = Object.keys(activeJobs).filter(id => !activeJobs[id].isPaused);
    if (activeJobIds.length === 0 || !token) return;
    
    const heartbeatInterval = setInterval(async () => {
      for (const jobId of activeJobIds) {
        try {
          await axios.post(`${API}/jobs/tracking-heartbeat`, 
            { job_id: jobId },
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } catch (error) {
          // Heartbeat failures are non-critical - log in development only
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Heartbeat failed for job ${jobId}:`, error.message);
          }
        }
      }
    }, 30000);
    
    return () => clearInterval(heartbeatInterval);
  }, [activeJobs, token]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  const handleDepartmentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/department-login`, { department, password });
      setDepartmentToken(response.data.department_token);
      setDepartmentInfo(response.data.department);
      setOperators(response.data.operators);
      
      const expiry = new Date();
      expiry.setHours(expiry.getHours() + 10);
      localStorage.setItem('workTerminalSession', JSON.stringify({
        departmentToken: response.data.department_token,
        departmentInfo: response.data.department,
        operators: response.data.operators,
        expiry: expiry.toISOString()
      }));
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid department or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOperator = async (operator) => {
    setLoading(true);
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/auth/select-operator`, {
        department_token: departmentToken,
        operator_id: operator.id
      });
      const authToken = response.data.access_token;
      setToken(authToken);
      setSelectedOperator({ ...operator, department: departmentInfo.name });
      localStorage.setItem('token', authToken);
      setStep(3);
      
      // Auto-sync from PrintLogic on operator login
      await syncFromPrintLogic(authToken);
      
      // Check for active jobs and auto-restore timer (pass operator_id for multi-user filtering)
      await restoreActiveJob(authToken, operator.id);
      
      await fetchJobs(authToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select operator');
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };
  
  // Sync jobs from PrintLogic ERP
  const syncFromPrintLogic = async (authToken) => {
    try {
      const response = await axios.post(`${API}/jobs/sync`, {}, {
        headers: { Authorization: `Bearer ${authToken || token}` },
        timeout: 60000 // 60 second timeout for sync
      });
      const result = response.data;
      if (result.synced > 0 || result.updated > 0) {
        toast.success(`Synced: ${result.synced} new, ${result.updated} updated jobs`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Sync failed:', error);
      }
      // Silent fail - proceed with cached data
    }
  };
  
  // Restore ALL active job tracking for this operator only
  const restoreActiveJob = async (authToken, operatorId) => {
    try {
      const response = await axios.get(`${API}/jobs/active-tracking`, {
        headers: { Authorization: `Bearer ${authToken || token}` }
      });
      const allActiveTracking = response.data.active_jobs || [];
      
      // Filter to only this operator's tracking entries (multi-user same-job support)
      const myActiveTracking = allActiveTracking.filter(t => t.operator_id === operatorId);
      
      if (myActiveTracking.length > 0) {
        // Fetch full job details
        const jobsResponse = await axios.get(`${API}/printlogic/synced-jobs?limit=500`, {
          headers: { Authorization: `Bearer ${authToken || token}` }
        });
        const allJobs = jobsResponse.data.jobs || [];
        
        // Restore only this operator's active jobs with their timers
        const restoredJobs = {};
        const now = new Date();
        
        for (const tracking of myActiveTracking) {
          const jobData = allJobs.find(j => j.job_id === tracking.job_id);
          if (jobData && tracking.started_at) {
            const startedAt = new Date(tracking.started_at);
            const elapsedSeconds = Math.floor((now - startedAt) / 1000);
            
            restoredJobs[tracking.job_id] = {
              job: jobData,
              timer: elapsedSeconds,
              isPaused: false,
              startedAt: tracking.started_at
            };
          }
        }
        
        if (Object.keys(restoredJobs).length > 0) {
          setActiveJobs(restoredJobs);
          const count = Object.keys(restoredJobs).length;
          toast.info(`Resumed tracking ${count} job${count > 1 ? 's' : ''}`);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to restore active jobs:', error);
      }
    }
  };

  const fetchJobs = async (authToken) => {
    try {
      const response = await axios.get(`${API}/printlogic/synced-jobs?limit=500`, {
        headers: { Authorization: `Bearer ${authToken || token}` }
      });
      let allJobs = response.data.jobs || [];
      
      const stage = departmentStageMap[departmentInfo?.name];
      if (stage) {
        allJobs = allJobs.filter(j => j.board_stage === stage && j.visible_on_board !== false);
      }
      
      setJobs(allJobs);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch jobs:', error);
      }
    }
  };

  const handleStartJob = async (job) => {
    // Check if job is already being tracked
    if (activeJobs[job.job_id]) {
      toast.info(`Job #${job.job_id} is already being tracked`);
      return;
    }
    
    try {
      await axios.post(`${API}/jobs/start-tracking`, 
        { job_id: job.job_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // NOTE: We do NOT sync to PrintLogic here - time tracking is internal only
      // The job's PrintLogic status should only be changed by explicit stage moves
      
      // Add to active jobs (don't replace - allow multiple)
      setActiveJobs(prev => ({
        ...prev,
        [job.job_id]: {
          job: job,
          timer: 0,
          isPaused: false,
          startedAt: new Date().toISOString()
        }
      }));
      
      toast.success(`Started working on job #${job.job_id}`);
      
      // Auto-revert to operator selection after 10 seconds
      // BUT keep ALL job tracking running on the backend
      setAutoRevertCountdown(10);
      let countdown = 10;
      const countdownInterval = setInterval(() => {
        countdown--;
        setAutoRevertCountdown(countdown);
        if (countdown <= 0) {
          clearInterval(countdownInterval);
          // Clear local UI state but DON'T stop backend tracking
          setSelectedOperator(null);
          setToken('');
          setActiveJobs({});  // Clear local display only
          setJobs([]);
          setAutoRevertCountdown(null);
          localStorage.removeItem('token');
          setStep(2);
          // Note: We do NOT stop tracking - all jobs continue on backend
        }
      }, 1000);
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to start tracking:', error);
      }
      toast.error('Failed to start job tracking');
    }
  };

  const handlePauseJob = (jobId) => {
    if (!activeJobs[jobId]) return;
    setActiveJobs(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], isPaused: true }
    }));
    toast.info(`Paused job #${jobId}`);
  };
  
  const handleResumeJob = (jobId) => {
    if (!activeJobs[jobId]) return;
    setActiveJobs(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], isPaused: false }
    }));
    toast.success(`Resumed job #${jobId}`);
  };

  const handleStopJob = async (jobId) => {
    const activeJobData = activeJobs[jobId];
    if (!activeJobData) return;
    
    try {
      await axios.post(`${API}/jobs/stop-tracking`,
        { job_id: jobId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await axios.post(`${API}/jobs/log-production-time`, {
        order_number: jobId,
        stage: activeJobData.job.board_stage,
        elapsed_seconds: activeJobData.timer,
        operator: selectedOperator.name,
        notes: ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Logged ${formatTime(activeJobData.timer)} on job #${jobId}`);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to stop tracking:', error);
      }
    }
    
    // Remove from active jobs
    setActiveJobs(prev => {
      const updated = { ...prev };
      delete updated[jobId];
      return updated;
    });
    
    fetchJobs();
  };

  const handleCompleteJob = async (job) => {
    // Stop tracking if active
    if (activeJobs[job.job_id]) {
      await handleStopJob(job.job_id);
    }
    
    try {
      await axios.post(`${API}/jobs/update-status`,
        { job_id: job.job_id, new_status: 'Complete', new_stage: job.board_stage, sync_to_printlogic: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Job #${job.job_id} marked complete`);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const handleOpenMoveModal = (job) => {
    setJobToMove(job);
    setShowMoveModal(true);
  };

  const handleMoveToDepartment = async (targetStage) => {
    if (!jobToMove) return;
    
    // Stop tracking if active
    // Stop tracking if active
    if (activeJobs[jobToMove.job_id]) {
      await handleStopJob(jobToMove.job_id);
    }
    
    try {
      await axios.post(`${API}/jobs/update-status`,
        { job_id: jobToMove.job_id, new_status: 'Ready', new_stage: targetStage, sync_to_printlogic: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const deptName = allDepartments.find(d => d.stage === targetStage)?.name || targetStage;
      toast.success(`Job #${jobToMove.job_id} moved to ${deptName}`);
      setShowMoveModal(false);
      setJobToMove(null);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to move job');
    }
  };

  const handleLogout = () => {
    // Stop all active jobs before logout
    Object.keys(activeJobs).forEach(jobId => {
      handleStopJob(jobId);
    });
    setStep(1);
    setDepartment('');
    setPassword('');
    setDepartmentToken('');
    setDepartmentInfo(null);
    setOperators([]);
    setSelectedOperator(null);
    setToken('');
    setJobs([]);
    setActiveJobs({});
    setAutoRevertCountdown(null);
    localStorage.removeItem('token');
    localStorage.removeItem('workTerminalSession');
  };

  // Step 1: Department Login
  if (step === 1) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-zinc-900 border-zinc-800">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-xl bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
              <Factory className="w-10 h-10 text-cyan-500" />
            </div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">Work Terminal</h1>
            <p className="text-zinc-500 mt-2">Enter your department to continue</p>
          </div>
          
          <form onSubmit={handleDepartmentLogin} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 mb-2 block">Department</label>
              <Input
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-14 text-xl"
                placeholder="e.g., Litho, Digital"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 mb-2 block">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-14 text-xl"
                placeholder="Department password"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-16 bg-cyan-600 hover:bg-cyan-500 text-xl font-bold"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Continue'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Step 2: Select Operator
  if (step === 2) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg p-8 bg-zinc-900 border-zinc-800">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setStep(1)} className="text-zinc-500 hover:text-white" disabled={loading || syncing}>
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-cyan-500 uppercase">{departmentInfo?.display_name}</h2>
            <div className="w-6" />
          </div>
          
          {syncing ? (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 text-cyan-500 mx-auto mb-4 animate-spin" />
              <p className="text-white text-xl font-bold">Syncing Jobs</p>
              <p className="text-zinc-400 mt-2">Fetching latest jobs from PrintLogic...</p>
            </div>
          ) : (
            <>
              <h3 className="text-center text-zinc-400 mb-6 uppercase text-sm">Select Your Name</h3>
              
              <div className="grid gap-3">
                {operators.map((op) => (
                  <button
                    key={op.id}
                    onClick={() => handleSelectOperator(op)}
                    disabled={loading}
                    className="w-full p-5 bg-zinc-950 border-2 border-zinc-800 rounded-lg hover:border-cyan-500 transition-colors flex items-center gap-4 disabled:opacity-50"
                  >
                    <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <User className="w-7 h-7 text-cyan-500" />
                    </div>
                    <span className="text-2xl font-bold text-white">{op.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    );
  }

  // Step 3: Job List & Time Tracking
  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <User className="w-6 h-6 text-cyan-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{selectedOperator?.name}</h1>
              <p className="text-sm text-cyan-500">{selectedOperator?.department}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-3xl font-bold text-white font-mono">
                {currentTime.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-zinc-700">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Active Jobs Banner - Shows ALL active jobs */}
      {Object.keys(activeJobs).length > 0 && (
        <div className="bg-green-900/30 border-b-2 border-green-500 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-400 animate-pulse" />
              <span className="text-green-400 font-bold uppercase text-sm">
                {Object.keys(activeJobs).length} Active Job{Object.keys(activeJobs).length > 1 ? 's' : ''}
              </span>
            </div>
            {autoRevertCountdown !== null && (
              <p className="text-amber-400 text-sm animate-pulse">
                Returning to operator selection in {autoRevertCountdown}s...
              </p>
            )}
          </div>
          <div className="grid gap-2">
            {Object.entries(activeJobs).map(([jobId, data]) => (
              <div 
                key={jobId}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  data.isPaused ? 'bg-amber-950/50 border border-amber-700' : 'bg-green-950/50 border border-green-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={data.isPaused ? '' : 'animate-pulse'}>
                    {data.isPaused ? <Pause className="w-5 h-5 text-amber-400" /> : <Play className="w-5 h-5 text-green-400" />}
                  </div>
                  <div>
                    <span className={`font-bold ${data.isPaused ? 'text-amber-400' : 'text-green-400'}`}>
                      #{jobId}
                    </span>
                    <span className="text-zinc-400 text-sm ml-2">{data.job.customer}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono font-bold text-xl ${data.isPaused ? 'text-amber-400' : 'text-green-400'}`}>
                    {formatTime(data.timer)}
                  </span>
                  {data.isPaused ? (
                    <Button onClick={() => handleResumeJob(jobId)} size="sm" className="bg-green-600 hover:bg-green-500">
                      <Play className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button onClick={() => handlePauseJob(jobId)} size="sm" className="bg-amber-600 hover:bg-amber-500">
                      <Pause className="w-4 h-4" />
                    </Button>
                  )}
                  <Button onClick={() => handleStopJob(jobId)} size="sm" className="bg-red-600 hover:bg-red-500">
                    <Square className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Job List */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Department Jobs Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-zinc-400 uppercase">
            {departmentInfo?.name} Jobs ({jobs.length})
          </h2>
          <Button onClick={() => fetchJobs()} variant="outline" className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {jobs.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 text-lg">No jobs in {departmentInfo?.name}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {jobs.map((job) => {
              const isActive = !!activeJobs[job.job_id];
              const activeData = activeJobs[job.job_id];
              const isLate = job.date_due && job.date_due !== '0000-00-00' && new Date(job.date_due) < new Date();
              
              return (
                <div
                  key={job.job_id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isActive ? 'bg-green-950 border-green-500' : isLate ? 'bg-red-950/50 border-red-700' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xl font-bold ${isActive ? 'text-green-400' : isLate ? 'text-red-400' : 'text-cyan-400'}`}>
                          #{job.job_id}
                        </span>
                        {isActive && <Badge className="bg-green-600">TRACKING</Badge>}
                        {job.quantity > 1 && <Badge className="bg-zinc-700">{job.quantity} qty</Badge>}
                        {isLate && <Badge className="bg-red-600">LATE</Badge>}
                      </div>
                      <p className="text-lg text-white font-medium mt-1">{job.customer || 'Customer'}</p>
                      <p className="text-zinc-400 truncate">{job.product || 'Product'}</p>
                      {isActive && activeData && (
                        <p className="text-green-400 font-mono text-sm mt-1">
                          Timer: {formatTime(activeData.timer)} {activeData.isPaused && '(paused)'}
                        </p>
                      )}
                      {job.date_due && job.date_due !== '0000-00-00' && (
                        <p className="text-sm text-zinc-500 mt-1">Due: {new Date(job.date_due).toLocaleDateString('en-IE')}</p>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 justify-end">
                      {!isActive ? (
                        <Button onClick={() => handleStartJob(job)} className="h-12 px-4 bg-green-600 hover:bg-green-500 font-bold">
                          <Play className="w-5 h-5 mr-1" /> Start
                        </Button>
                      ) : (
                        <>
                          {activeData?.isPaused ? (
                            <Button onClick={() => handleResumeJob(job.job_id)} className="h-12 px-4 bg-green-600 hover:bg-green-500 font-bold">
                              <Play className="w-5 h-5 mr-1" /> Resume
                            </Button>
                          ) : (
                            <Button onClick={() => handlePauseJob(job.job_id)} className="h-12 px-4 bg-amber-600 hover:bg-amber-500 font-bold">
                              <Pause className="w-5 h-5 mr-1" /> Pause
                            </Button>
                          )}
                          <Button onClick={() => handleStopJob(job.job_id)} className="h-12 px-4 bg-red-600 hover:bg-red-500 font-bold">
                            <Square className="w-5 h-5 mr-1" /> Stop
                          </Button>
                        </>
                      )}
                      <Button onClick={() => handleCompleteJob(job)} className="h-12 px-4 bg-blue-600 hover:bg-blue-500" title="Mark Complete">
                        <CheckCircle className="w-5 h-5" />
                      </Button>
                      <Button onClick={() => handleOpenMoveModal(job)} variant="outline" className="h-12 px-4 border-zinc-700 hover:bg-zinc-800" title="Move to Department">
                        <ArrowRightCircle className="w-5 h-5 mr-1" /> Move
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Move to Department Modal */}
      <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Move Job #{jobToMove?.job_id} to Department</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 mt-4">
            {allDepartments.map((dept) => (
              <button
                key={dept.stage}
                onClick={() => handleMoveToDepartment(dept.stage)}
                disabled={jobToMove?.board_stage === dept.stage}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  jobToMove?.board_stage === dept.stage
                    ? 'bg-zinc-800 border-zinc-700 text-zinc-500 cursor-not-allowed'
                    : 'bg-zinc-950 border-zinc-700 hover:border-cyan-500 text-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium">{dept.name}</span>
                  {jobToMove?.board_stage === dept.stage && <Badge className="bg-zinc-700">Current</Badge>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 p-3 text-center">
        <p className="text-zinc-600 text-sm">{jobs.length} jobs • Auto-refreshes on action</p>
      </footer>
      
      <Toaster position="top-center" theme="dark" />
    </div>
  );
};

export default OperatorWorkTerminal;
